import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Dropdown,
  Option,
  Checkbox,
  Tag,
  TagGroup,
} from '@fluentui/react-components';
import {
  LinkRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { TenderingPart } from '../../../models/tendering';
import { Filter } from '../../../models/filters';
import QuestionDialog from '../../dialogs/QuestionDialog';

interface FilterSelection {
  filterID: string;
  type: string;
  value: string;
  text: string;
  questionID?: number;
}

interface TenderingAdminFilterProps {
  filter: Filter;
  tenderingParts: TenderingPart[];
  isBaseFilter: boolean;
  baseQuestionIDs: number[];
  onFilterChange?: (filter: Filter) => void;
}

const TenderingAdminFilter: React.FC<TenderingAdminFilterProps> = ({
  filter,
  tenderingParts,
  isBaseFilter,
  baseQuestionIDs,
  onFilterChange,
}) => {
  const [choices, setChoices] = useState<FilterSelection[]>([]);
  const [newChoiceText, setNewChoiceText] = useState('');
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [questionModalData, setQuestionModalData] = useState<any>(null);
  const [boundQuestionModalOpen, setBoundQuestionModalOpen] = useState(false);
  
  const filtersTenderingParts = tenderingParts.filter(tp => tp.listPrio);

  const filterTypes = [
    { label: 'Ja/Nej', value: 'bool' },
    { label: 'Val', value: 'select' },
    { label: 'Fritext', value: 'text' },
    { label: 'Numerisk', value: 'int' },
    { label: 'Numerisk intervall', value: 'int-range' },
  ];

  const answerTypes = [
    { label: 'Ja/Nej', value: 'bool' },
    { label: 'Fritext', value: 'text' },
    { label: 'Numerisk', value: 'int' },
  ];

  useEffect(() => {
    onTypeChange();
  }, [filter.type]);

  const uuidv4 = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const updateFilter = (updates: Partial<Filter>) => {
    const updatedFilter = { ...filter, ...updates };
    if (onFilterChange) {
      onFilterChange(updatedFilter);
    }
  };

  const onTypeChange = () => {
    switch (filter.type) {
      case 'select':
        setChoices(filter.selections || []);
        if (filter.separateQuestions === undefined) {
          updateFilter({ separateQuestions: false });
        }
        if (!filter.heading) {
          updateFilter({ heading: '' });
        }
        if (!filter.description) {
          updateFilter({ description: '' });
        }
        if (!filter.questionType) {
          updateFilter({ questionType: 'text' });
        }
        break;
      case 'int-range':
        if (!filter.min) {
          updateFilter({ min: 0 });
        }
        if (!filter.max) {
          updateFilter({ max: 100 });
        }
        setChoices(filter.selections || []);
        break;
      default:
        setChoices(filter.selections || []);
        break;
    }
  };

  const addChoice = () => {
    if (newChoiceText.trim()) {
      const newChoice: FilterSelection = {
        filterID: uuidv4(),
        type: filter.questionType || 'text',
        value: newChoiceText.trim(),
        text: newChoiceText.trim(),
      };
      const newChoices = [...choices, newChoice];
      setChoices(newChoices);
      updateFilter({ selections: newChoices });
      setNewChoiceText('');
    }
  };

  const removeChoice = (choice: FilterSelection) => {
    const newChoices = choices.filter(c => c.filterID !== choice.filterID);
    setChoices(newChoices);
    updateFilter({ selections: newChoices });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addChoice();
    }
  };

  const openQuestionPage = (data: any) => {
    setQuestionModalData(data);
    setQuestionModalOpen(true);
  };

  const openBoundQuestionPage = (data: any) => {
    setQuestionModalData(data);
    setBoundQuestionModalOpen(true);
  };

  const handleQuestionSelect = (data: any) => {
    if (!data || !data.selected || data.selected.length === 0) {
      setQuestionModalOpen(false);
      return;
    }

    if (filter.separateQuestions) {
      const filterSelection = choices.find(s => s.filterID === data.selected[0].filterID);
      if (filterSelection) {
        filterSelection.questionID = data.selected[0].questionID;
        updateFilter({ selections: [...choices] });
      }
    } else if (filter.type === 'int-range') {
      const q = data.selected[0].questionID;
      if (data.returnData === 'min') {
        updateFilter({ minQuestionID: q });
      } else {
        updateFilter({ maxQuestionID: q });
      }
    } else {
      updateFilter({ questionID: data.selected[0].questionID });
    }

    setQuestionModalOpen(false);
  };

  const handleBoundQuestionSelect = (data: any) => {
    if (!data || !data.selected || data.selected.length === 0) {
      setBoundQuestionModalOpen(false);
      return;
    }

    updateFilter({ boundQuestionID: data.selected[0].questionID });
    setBoundQuestionModalOpen(false);
  };

  const handleTenderingPartChange = (selectedIds: string[]) => {
    const selected = filtersTenderingParts.filter(tp => 
      selectedIds.includes(tp.tenderingPartID.toString())
    );
    updateFilter({ tenderingPart: selected });
  };

  return (
    <div className="space-y-4 w-full">
      {/* Filter Type */}
      <div>
        <h5 className="text-sm font-semibold mb-2">Filtertyp</h5>
        <Dropdown
          placeholder="Välj filtertyp"
          value={filterTypes.find(ft => ft.value === filter.type)?.label || ''}
          onOptionSelect={(_, data) => updateFilter({ type: data.optionValue as string })}
        >
          {filterTypes.map((ft) => (
            <Option key={ft.value} value={ft.value}>
              {ft.label}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* Delområde (for base filters) */}
      {isBaseFilter && (
        <div>
          <h5 className="text-sm font-semibold mb-2">Delområde</h5>
          <Dropdown
            placeholder="Välj delområde"
            multiselect
            value={
              filter.tenderingPart?.map((tp: TenderingPart) => tp.tenderingPart).join(', ') || ''
            }
            selectedOptions={filter.tenderingPart?.map((tp: TenderingPart) => tp.tenderingPartID.toString()) || []}
            onOptionSelect={(_, data) => {
              handleTenderingPartChange(data.selectedOptions);
            }}
          >
            {filtersTenderingParts.map((tp) => (
              <Option key={tp.tenderingPartID} value={tp.tenderingPartID.toString()}>
                {tp.tenderingPart}
              </Option>
            ))}
          </Dropdown>
        </div>
      )}

      {/* Select Type Configuration */}
      {filter.type === 'select' && (
        <>
          <div>
            <h5 className="text-sm font-semibold mb-2">Lägg till val</h5>
            <div className="mb-2">
              <TagGroup onDismiss={(_, data) => {
                const choice = choices.find(c => c.filterID === data.value);
                if (choice) removeChoice(choice);
              }}>
                {choices.map((choice) => (
                  <Tag
                    key={choice.filterID}
                    value={choice.filterID}
                    dismissible
                    dismissIcon={<DismissRegular />}
                  >
                    <div className="flex items-center gap-2">
                      <span>{choice.text}</span>
                      {filter.separateQuestions && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<LinkRegular />}
                          onClick={() => openQuestionPage(choice)}
                          title="Mappa fråga"
                          className={choice.questionID ? 'text-blue-600' : ''}
                        />
                      )}
                    </div>
                  </Tag>
                ))}
              </TagGroup>
            </div>
            <Input
              placeholder="Nytt val..."
              value={newChoiceText}
              onChange={(e) => setNewChoiceText(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full"
            />
          </div>

          <div>
            <Checkbox
              checked={filter.separateQuestions || false}
              onChange={(_, data) => updateFilter({ separateQuestions: data.checked as boolean })}
              label="Kopplas till separata frågor"
            />
          </div>

          {!filter.separateQuestions && (
            <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
              <Button
                appearance="subtle"
                icon={<LinkRegular />}
                onClick={() =>
                  openQuestionPage({ questionID: filter.questionID, filterID: filter.filterID })
                }
                title="Mappa alla val till fråga"
              />
              <div className="text-sm">
                {filter.questionID
                  ? `Kopplad till fråga: ${filter.questionID}`
                  : 'Ej kopplad till fråga'}
              </div>
            </div>
          )}

          <div>
            <h5 className="text-sm font-semibold mb-2">Svarstyp</h5>
            <Dropdown
              placeholder="Välj svarstyp"
              value={answerTypes.find(at => at.value === filter.questionType)?.label || ''}
              onOptionSelect={(_, data) => updateFilter({ questionType: data.optionValue as string })}
            >
              {answerTypes.map((at) => (
                <Option key={at.value} value={at.value}>
                  {at.label}
                </Option>
              ))}
            </Dropdown>
          </div>
        </>
      )}

      {/* Int Range Configuration */}
      {filter.type === 'int-range' && (
        <>
          <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
            <div className="font-medium">Min värde fråga:</div>
            <Button
              appearance="subtle"
              icon={<LinkRegular />}
              onClick={() =>
                openQuestionPage({
                  questionID: filter.minQuestionID,
                  returnData: 'min',
                  filterID: filter.filterID,
                })
              }
              title="Koppla till min värde fråga"
            />
            <div className="text-sm">
              {filter.minQuestionID
                ? `Kopplad till fråga: ${filter.minQuestionID}`
                : 'Ej kopplad till fråga'}
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
            <div className="font-medium">Max värde fråga:</div>
            <Button
              appearance="subtle"
              icon={<LinkRegular />}
              onClick={() =>
                openQuestionPage({
                  questionID: filter.maxQuestionID,
                  returnData: 'max',
                  filterID: filter.filterID,
                })
              }
              title="Koppla till max värde fråga"
            />
            <div className="text-sm">
              {filter.maxQuestionID
                ? `Kopplad till fråga: ${filter.maxQuestionID}`
                : 'Ej kopplad till fråga'}
            </div>
          </div>

          <div>
            <h5 className="text-sm font-semibold mb-2">Min värde</h5>
            <Input
              type="number"
              value={filter.min?.toString() || '0'}
              onChange={(e) => updateFilter({ min: Number(e.target.value) })}
              className="w-full"
            />
          </div>

          <div>
            <h5 className="text-sm font-semibold mb-2">Max värde</h5>
            <Input
              type="number"
              value={filter.max?.toString() || '100'}
              onChange={(e) => updateFilter({ max: Number(e.target.value) })}
              className="w-full"
            />
          </div>
        </>
      )}

      {/* Simple Question Mapping (text, int, bool) */}
      {(filter.type === 'text' || filter.type === 'int' || filter.type === 'bool') && (
        <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
          <Button
            appearance="subtle"
            icon={<LinkRegular />}
            onClick={() =>
              openQuestionPage({ questionID: filter.questionID, filterID: filter.filterID })
            }
            title="Koppla till fråga"
          />
          <div className="text-sm">
            {filter.questionID ? `Kopplad till fråga: ${filter.questionID}` : 'Ej kopplad till fråga'}
          </div>
        </div>
      )}

      {/* Bound Question */}
      <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
        <Button
          appearance="subtle"
          icon={<LinkRegular />}
          onClick={() =>
            openBoundQuestionPage({ questionID: filter.questionID, filterID: filter.filterID })
          }
          title="Beroende fråga"
        />
        <div className="text-sm">
          {filter.boundQuestionID
            ? `Beroende fråga: ${filter.boundQuestionID}`
            : 'Ej beroende fråga'}
        </div>
      </div>

      {/* Name */}
      <div>
        <h5 className="text-sm font-semibold mb-2">Namn</h5>
        <Input
          className="w-full"
          value={filter.text || ''}
          onChange={(e) => updateFilter({ text: e.target.value })}
        />
      </div>

      {/* Description */}
      <div>
        <h5 className="text-sm font-semibold mb-2">Beskrivning</h5>
        <textarea
          className="w-full border border-gray-300 rounded p-2 min-h-[80px]"
          value={filter.description || ''}
          onChange={(e) => updateFilter({ description: e.target.value })}
        />
      </div>

      {/* Required Checkbox */}
      <div>
        <Checkbox
          checked={filter.isRequired || false}
          onChange={(_, data) => updateFilter({ isRequired: data.checked as boolean })}
          label="Obligatorisk"
        />
      </div>

      {/* Question Dialog */}
      {questionModalOpen && (
        <QuestionDialog
          isOpen={questionModalOpen}
          onClose={() => setQuestionModalOpen(false)}
          onSelect={handleQuestionSelect}
          tenderingParts={tenderingParts}
          singleSelect={true}
          selectedQuestions={
            questionModalData?.questionID ? [{ questionID: questionModalData.questionID }] : undefined
          }
          filterID={questionModalData?.filterID}
          returnData={questionModalData?.returnData}
        />
      )}

      {/* Bound Question Modal */}
      {boundQuestionModalOpen && (
        <QuestionDialog
          isOpen={boundQuestionModalOpen}
          onClose={() => setBoundQuestionModalOpen(false)}
          onSelect={handleBoundQuestionSelect}
          tenderingParts={tenderingParts}
          singleSelect={true}
          selectedQuestions={
            filter.boundQuestionID ? [{ questionID: filter.boundQuestionID }] : undefined
          }
          filterID={questionModalData?.filterID}
          questionIDs={baseQuestionIDs}
        />
      )}
    </div>
  );
};

export default TenderingAdminFilter;
